/**
 * Partner API Console — Admin UI for managing external Watcho API partners.
 *
 * Tabs:
 *   • Partners     — CRUD, manage allowed IPs, reveal api_key/secret, rotate
 *                    secret, edit per-plan markups, view wallet balance.
 *   • Wallet       — Per-partner credit/debit + transactions ledger.
 *   • Activations  — All activations created via the external API.
 *   • API Logs     — Audit trail of every external API hit.
 *   • Docs         — Quick-reference integration guide for partners.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Users, KeyRound, Wallet, RefreshCw, Plus, Trash2, Copy, LogIn,
  Eye, EyeOff, IndianRupee, FileText, Activity, ShieldCheck,
  Wifi, WifiOff, RotateCw, Zap,
} from 'lucide-react';
import PartnerAnalyticsPanel from './partner-portal/PartnerAnalyticsPanel';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtMoney = (n) => `Rs.${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('en-IN') : '-';

const StatusBadge = ({ status }) => {
  const map = {
    activated: { v: 'default', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    failed:    { v: 'destructive', cls: 'bg-rose-100 text-rose-700 border-rose-200' },
    pending:   { v: 'secondary', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  };
  const m = map[status] || map.pending;
  return <Badge variant={m.v} className={m.cls}>{status}</Badge>;
};

export default function PartnerAPIConsole() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [tab, setTab] = useState('partners');
  const [stats, setStats] = useState(null);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [health, setHealth] = useState(null);

  const loadHealth = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/partner-api/watcho-health`, { headers });
      setHealth(r.data);
    } catch { setHealth({ ok: false, detail: 'probe failed' }); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadStats = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/partner-api/stats`, { headers });
      setStats(r.data);
    } catch { /* */ }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPartners = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/partner-api/partners`, { headers });
      setPartners(r.data.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load partners');
    }
    setLoading(false);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadStats(); loadPartners(); }, [loadStats, loadPartners]);

  useEffect(() => {
    loadHealth();
    const id = setInterval(loadHealth, 60000);
    return () => clearInterval(id);
  }, [loadHealth]);

  // Convenience alias used by various mutation handlers below.
  const reload = useCallback(() => { loadStats(); loadPartners(); }, [loadStats, loadPartners]);

  return (
    <div className="space-y-6 p-6" data-testid="partner-api-console">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Partner API Console</h1>
          <p className="text-sm text-slate-500">External Watcho activation API · IP whitelist + HMAC signed</p>
        </div>
        <div className="flex items-center gap-2">
          <WatchoHealthBadge health={health} onRefresh={loadHealth} />
          <Button variant="outline" size="sm" onClick={() => { loadStats(); loadPartners(); }} data-testid="refresh-partner-api-btn">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <StatCard label="Active Partners" value={stats.partners_active} icon={Users} color="text-sky-700" />
          <StatCard label="Inactive" value={stats.partners_inactive} icon={ShieldCheck} color="text-slate-500" />
          <StatCard label="Calls Today" value={stats.calls_today} icon={Activity} color="text-violet-700" />
          <StatCard label="Activated Today" value={stats.activations_today} icon={KeyRound} color="text-emerald-700" />
          <StatCard label="Failed Today" value={stats.failed_today} icon={Activity} color="text-rose-700" />
          <StatCard label="Total Wallet" value={fmtMoney(stats.total_wallet_balance)} icon={IndianRupee} color="text-slate-900" />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="partners" data-testid="tab-partners"><Users className="h-4 w-4 mr-1" />Partners</TabsTrigger>
          <TabsTrigger value="ledger" data-testid="tab-ledger"><IndianRupee className="h-4 w-4 mr-1" />Ledger</TabsTrigger>
          <TabsTrigger value="activations" data-testid="tab-activations"><KeyRound className="h-4 w-4 mr-1" />Activations</TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs"><Activity className="h-4 w-4 mr-1" />API Logs</TabsTrigger>
          <TabsTrigger value="docs" data-testid="tab-docs"><FileText className="h-4 w-4 mr-1" />Integration Docs</TabsTrigger>
        </TabsList>

        <TabsContent value="partners" className="mt-4">
          <PartnersTab partners={partners} reload={loadPartners} loading={loading} headers={headers} />
        </TabsContent>
        <TabsContent value="ledger" className="mt-4">
          <LedgerTab headers={headers} partners={partners} />
        </TabsContent>
        <TabsContent value="activations" className="mt-4">
          <ActivationsTab headers={headers} partners={partners} />
        </TabsContent>
        <TabsContent value="logs" className="mt-4">
          <LogsTab headers={headers} partners={partners} />
        </TabsContent>
        <TabsContent value="docs" className="mt-4">
          <DocsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

const StatCard = ({ label, value, icon: Icon, color }) => (
  <Card><CardContent className="p-3">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
        <p className={`text-xl font-bold ${color || ''}`}>{value}</p>
      </div>
      {Icon && <Icon className="h-7 w-7 text-slate-200" />}
    </div>
  </CardContent></Card>
);

// ============== Watcho Health Badge ==============
const WatchoHealthBadge = ({ health, onRefresh }) => {
  if (!health) {
    return <Badge variant="outline" className="bg-slate-50 text-slate-500" data-testid="watcho-health-badge">Watcho: checking…</Badge>;
  }
  const ok = health.ok;
  return (
    <button
      onClick={onRefresh}
      title={`${health.detail || ''}${health.latency_ms != null ? ` · ${health.latency_ms}ms` : ''} (click to re-check)`}
      data-testid="watcho-health-badge"
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
        ok ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
           : 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100'
      }`}
    >
      {ok ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
      Watcho {ok ? 'Online' : 'Down'}
      {ok && health.latency_ms != null && <span className="opacity-60">· {health.latency_ms}ms</span>}
    </button>
  );
};

// ============== Ledger Tab (consolidated, till-date) ==============
function LedgerTab({ headers, partners }) {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [partnerId, setPartnerId] = useState('all');
  const [category, setCategory] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '500' });
      if (partnerId && partnerId !== 'all') params.set('partner_id', partnerId);
      if (category && category !== 'all') params.set('category', category);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const r = await axios.get(`${API}/api/partner-api/ledger?${params.toString()}`, { headers });
      setRows(r.data.rows || []);
      setTotals(r.data.totals || null);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load ledger');
    }
    setLoading(false);
  }, [partnerId, category, dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const head = ['When', 'Partner', 'Category', 'Type', 'Amount', 'Balance After', 'Reference', 'Description'];
    const catLabel = { debit: 'Debited', credit_manual: 'Credited Manually', credit_failure: 'Credited for Failure' };
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = rows.map(t => [
      fmtDate(t.created_at), t.partner_name, catLabel[t.category] || t.category,
      t.type, t.amount, t.balance_after, t.reference_id || t.reference || '', t.description || '',
    ].map(esc).join(','));
    const csv = [head.map(esc).join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `partner-ledger-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const catBadge = (c) => {
    if (c === 'debit') return <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">Debited</Badge>;
    if (c === 'credit_failure') return <Badge className="bg-amber-100 text-amber-700 border-amber-200">Failure Refund</Badge>;
    return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Manual Credit</Badge>;
  };

  return (
    <div className="space-y-4" data-testid="ledger-tab-panel">
      {/* Filters */}
      <Card><CardContent className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[200px]">
            <Label className="text-xs">Partner</Label>
            <Select value={partnerId} onValueChange={setPartnerId}>
              <SelectTrigger data-testid="ledger-partner-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Partners</SelectItem>
                {partners.map(p => <SelectItem key={p.partner_id} value={p.partner_id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[180px]">
            <Label className="text-xs">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="ledger-category-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="debit">Debited</SelectItem>
                <SelectItem value="credit_manual">Credited Manually</SelectItem>
                <SelectItem value="credit_failure">Credited for Failure</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">From</Label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-testid="ledger-date-from" className="w-[150px]" />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} data-testid="ledger-date-to" className="w-[150px]" />
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="ledger-refresh-btn"><RefreshCw className="h-4 w-4 mr-1" />Apply</Button>
          {(dateFrom || dateTo || partnerId !== 'all' || category !== 'all') && (
            <Button variant="ghost" size="sm" onClick={() => { setPartnerId('all'); setCategory('all'); setDateFrom(''); setDateTo(''); }}>Clear</Button>
          )}
          <div className="ml-auto">
            <Button size="sm" onClick={exportCsv} disabled={!rows.length} data-testid="ledger-export-csv"><FileText className="h-4 w-4 mr-1" />Export CSV</Button>
          </div>
        </div>
      </CardContent></Card>

      {/* Summary totals */}
      {totals && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <StatCard label={`Debited (${totals.debit_count})`} value={fmtMoney(totals.debit)} icon={IndianRupee} color="text-rose-700" />
          <StatCard label={`Credited Manually (${totals.credit_manual_count})`} value={fmtMoney(totals.credit_manual)} icon={IndianRupee} color="text-emerald-700" />
          <StatCard label={`Credited for Failure (${totals.credit_failure_count})`} value={fmtMoney(totals.credit_failure)} icon={IndianRupee} color="text-amber-700" />
        </div>
      )}

      {/* Ledger table */}
      <Card><CardContent className="p-0">
        <div className="border rounded max-h-[560px] overflow-y-auto" data-testid="ledger-consolidated-table">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 sticky top-0 z-10"><tr>
              <th className="text-left p-2">When</th>
              <th className="text-left p-2">Partner</th>
              <th className="text-left p-2">Category</th>
              <th className="text-right p-2">Amount</th>
              <th className="text-right p-2">Balance After</th>
              <th className="text-left p-2">Description</th>
            </tr></thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="text-center py-8 text-slate-400">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-400">No transactions</td></tr>}
              {!loading && rows.map(t => (
                <tr key={t.txn_id} className="border-t hover:bg-slate-50">
                  <td className="p-2 text-slate-500 whitespace-nowrap">{fmtDate(t.created_at)}</td>
                  <td className="p-2 font-medium text-slate-700">{t.partner_name}</td>
                  <td className="p-2">{catBadge(t.category)}</td>
                  <td className={`p-2 text-right font-semibold ${t.type === 'credit' ? 'text-emerald-700' : 'text-rose-700'}`}>{t.type === 'credit' ? '+' : '−'}{fmtMoney(t.amount)}</td>
                  <td className="p-2 text-right">{fmtMoney(t.balance_after)}</td>
                  <td className="p-2 text-slate-600">{t.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent></Card>
      <p className="text-xs text-slate-400">Showing {rows.length} of {total} matching transactions (most recent first).</p>
    </div>
  );
}

// ============== Partners Tab ==============
function PartnersTab({ partners, reload, loading, headers }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = {
    name: '', contact_name: '', contact_email: '', contact_phone: '',
    allowed_ips: '', rate_limit_per_min: 60, default_markup_pct: 20,
    plan_markups_text: '', is_active: true, notes: '',
  };
  const [form, setForm] = useState(empty);
  const [showSecretFor, setShowSecretFor] = useState({});
  const [selectedForWallet, setSelectedForWallet] = useState(null);

  // Portal credentials dialog
  const [credsDialog, setCredsDialog] = useState({ open: false, partner: null });
  const [credsForm, setCredsForm] = useState({ portal_email: '', password: '' });

  const openPortalCreds = (p) => {
    setCredsForm({ portal_email: p.portal_email || '', password: '' });
    setCredsDialog({ open: true, partner: p });
  };
  const savePortalCreds = async () => {
    const p = credsDialog.partner;
    if (!credsForm.portal_email || credsForm.password.length < 8) {
      toast.error('Email + password (min 8 chars) required');
      return;
    }
    try {
      await axios.post(`${API}/api/partner-api/partners/${p.partner_id}/portal-credentials`,
                       credsForm, { headers });
      toast.success(`Portal login set for ${p.name}`);
      window.alert(`Send these credentials to the partner:\n\nPortal URL: ${window.location.origin}/partner-portal/login\nEmail: ${credsForm.portal_email}\nPassword: ${credsForm.password}`);
      setCredsDialog({ open: false, partner: null });
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to set credentials');
    }
  };
  const revokePortalCreds = async () => {
    const p = credsDialog.partner;
    if (!window.confirm(`Revoke portal access for ${p.name}?`)) return;
    try {
      await axios.delete(`${API}/api/partner-api/partners/${p.partner_id}/portal-credentials`, { headers });
      toast.success('Portal access revoked');
      setCredsDialog({ open: false, partner: null });
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const openCreate = () => { setEditing(null); setForm(empty); setShowForm(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      ...empty,
      name: p.name || '',
      contact_name: p.contact_name || '',
      contact_email: p.contact_email || '',
      contact_phone: p.contact_phone || '',
      allowed_ips: (p.allowed_ips || []).join(', '),
      rate_limit_per_min: p.rate_limit_per_min || 60,
      default_markup_pct: p.default_markup_pct ?? 20,
      plan_markups_text: Object.entries(p.plan_markups || {}).map(([k, v]) => `${k}=${v}`).join('\n'),
      is_active: p.is_active !== false,
      notes: p.notes || '',
    });
    setShowForm(true);
  };

  const save = async () => {
    try {
      const plan_markups = {};
      (form.plan_markups_text || '').split('\n').forEach(line => {
        const [k, v] = line.split('=').map(s => s.trim());
        if (k && v) plan_markups[k] = parseFloat(v);
      });
      const payload = {
        name: form.name.trim(),
        contact_name: form.contact_name || null,
        contact_email: form.contact_email || null,
        contact_phone: form.contact_phone || null,
        allowed_ips: form.allowed_ips.split(',').map(s => s.trim()).filter(Boolean),
        rate_limit_per_min: parseInt(form.rate_limit_per_min) || 60,
        default_markup_pct: form.default_markup_pct === '' ? null : parseFloat(form.default_markup_pct),
        plan_markups,
        is_active: !!form.is_active,
        notes: form.notes || null,
      };
      if (editing) {
        await axios.put(`${API}/api/partner-api/partners/${editing.partner_id}`, payload, { headers });
        toast.success('Partner updated');
      } else {
        await axios.post(`${API}/api/partner-api/partners`, payload, { headers });
        toast.success('Partner created — secret displayed only on creation, save it now');
      }
      setShowForm(false);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const rotateSecret = async (p) => {
    if (!window.confirm(`Rotate API secret for ${p.name}? Old secret will stop working immediately.`)) return;
    try {
      const r = await axios.post(`${API}/api/partner-api/partners/${p.partner_id}/rotate-secret`, {}, { headers });
      toast.success('Secret rotated — copy from the dialog');
      window.alert(`New secret for ${p.name}:\n\n${r.data.api_secret}\n\nThis is the only time it's shown in full like this.`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Rotation failed'); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Deactivate partner ${p.name}?`)) return;
    try {
      await axios.delete(`${API}/api/partner-api/partners/${p.partner_id}`, { headers });
      toast.success('Partner deactivated');
      reload();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={openCreate} data-testid="new-partner-btn"><Plus className="h-4 w-4 mr-1" />New Partner</Button>
      </div>

      <Card><CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3">Partner</th>
                <th className="text-left px-3 py-3">API Key</th>
                <th className="text-left px-3 py-3">Allowed IPs</th>
                <th className="text-right px-3 py-3">Wallet</th>
                <th className="text-right px-3 py-3">Rate / min</th>
                <th className="text-left px-3 py-3">Last Hit</th>
                <th className="text-right px-3 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {partners.length === 0 && !loading && (
                <tr><td colSpan={7} className="text-center py-10 text-slate-400">No partners yet. Click "New Partner" above.</td></tr>
              )}
              {partners.map(p => (
                <tr key={p.partner_id} className="border-t hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{p.name}</div>
                    <div className="text-xs text-slate-500">{p.contact_email || p.contact_phone || ''}</div>
                    {p.is_active === false && <Badge variant="destructive" className="mt-1">Inactive</Badge>}
                  </td>
                  <td className="px-3 py-3 font-mono text-xs">
                    <div className="flex items-center gap-1">
                      <code>{showSecretFor[p.partner_id] ? p.api_key : `${(p.api_key || '').substring(0, 14)}…`}</code>
                      <Button variant="ghost" size="sm" onClick={() => setShowSecretFor(s => ({ ...s, [p.partner_id]: !s[p.partner_id] }))}>
                        {showSecretFor[p.partner_id] ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => copy(p.api_key, 'API key')}><Copy className="h-3.5 w-3.5" /></Button>
                    </div>
                    {showSecretFor[p.partner_id] && (
                      <div className="flex items-center gap-1 mt-1 text-slate-400">
                        <code>{p.api_secret}</code>
                        <Button variant="ghost" size="sm" onClick={() => copy(p.api_secret, 'API secret')}><Copy className="h-3.5 w-3.5" /></Button>
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3 text-xs">
                    {(p.allowed_ips || []).map(ip => <Badge key={ip} variant="outline" className="mr-1 mb-1 font-mono">{ip}</Badge>)}
                  </td>
                  <td className="px-3 py-3 text-right font-semibold">{fmtMoney(p.wallet_balance)}</td>
                  <td className="px-3 py-3 text-right">{p.rate_limit_per_min || 60}</td>
                  <td className="px-3 py-3 text-xs text-slate-500">{fmtDate(p.last_api_hit_at)}<br /><span className="font-mono">{p.last_api_hit_ip || ''}</span></td>
                  <td className="px-3 py-3 text-right space-x-1 whitespace-nowrap">
                    <Button variant="outline" size="sm" onClick={() => setSelectedForWallet(p)}><Wallet className="h-3.5 w-3.5 mr-1" />Wallet</Button>
                    <Button variant="outline" size="sm" onClick={() => openEdit(p)}>Edit</Button>
                    <Button variant="outline" size="sm" onClick={() => openPortalCreds(p)} title="Set Portal Login"><LogIn className="h-3.5 w-3.5" /></Button>
                    <Button variant="outline" size="sm" onClick={() => rotateSecret(p)} title="Rotate secret"><KeyRound className="h-3.5 w-3.5" /></Button>
                    <Button variant="outline" size="sm" onClick={() => remove(p)} className="text-rose-600"><Trash2 className="h-3.5 w-3.5" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent></Card>

      {/* Analytics Panel */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Activity className="h-4 w-4" /> Partner Analytics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <PartnerAnalyticsPanel
            endpoint={`${API}/api/partner-api/analytics`}
            authHeaders={headers}
            showPartnerSelector={true}
            partners={partners}
          />
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? 'Edit Partner' : 'New Partner'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Partner Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="partner-name-input" /></div>
            <div><Label>Contact Name</Label><Input value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} /></div>
            <div><Label>Contact Phone</Label><Input value={form.contact_phone} onChange={e => setForm({ ...form, contact_phone: e.target.value })} /></div>
            <div className="col-span-2"><Label>Contact Email</Label><Input value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })} /></div>
            <div className="col-span-2">
              <Label>Allowed IPs (comma-separated)</Label>
              <Input value={form.allowed_ips} placeholder="103.210.132.78, 1.2.3.4" onChange={e => setForm({ ...form, allowed_ips: e.target.value })} data-testid="partner-ips-input" />
              <p className="text-xs text-slate-500 mt-1">Requests are rejected with 403 unless source IP matches one of these exactly.</p>
            </div>
            <div><Label>Rate Limit (req/min)</Label><Input type="number" value={form.rate_limit_per_min} onChange={e => setForm({ ...form, rate_limit_per_min: e.target.value })} /></div>
            <div><Label>Default Markup % (over Watcho price)</Label><Input type="number" step="0.01" value={form.default_markup_pct} onChange={e => setForm({ ...form, default_markup_pct: e.target.value })} /></div>
            <div className="col-span-2">
              <Label>Per-Plan Partner Price (one per line, <code>plan_id=price</code>)</Label>
              <Textarea rows={4} value={form.plan_markups_text} placeholder={'170475=50\n170476=120'} onChange={e => setForm({ ...form, plan_markups_text: e.target.value })} />
              <p className="text-xs text-slate-500 mt-1">Absolute Rs. price the partner pays per plan. Overrides the Default Markup % when set for that plan.</p>
            </div>
            <div className="col-span-2 flex items-center gap-3">
              <Switch checked={form.is_active} onCheckedChange={c => setForm({ ...form, is_active: c })} id="is-active" />
              <Label htmlFor="is-active">Active</Label>
            </div>
            <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={save} disabled={!form.name} data-testid="save-partner-btn">{editing ? 'Update' : 'Create Partner'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {selectedForWallet && (
        <WalletDialog partner={selectedForWallet} headers={headers} onClose={() => { setSelectedForWallet(null); reload(); }} />
      )}

      {/* Portal Credentials Dialog */}
      <Dialog open={credsDialog.open} onOpenChange={(o) => setCredsDialog({ open: o, partner: credsDialog.partner })}>
        <DialogContent className="max-w-md" data-testid="portal-creds-dialog">
          <DialogHeader>
            <DialogTitle>Partner Portal Login</DialogTitle>
            <DialogDescription>
              Set the email & password for <span className="font-medium">{credsDialog.partner?.name}</span> to sign in at <code className="text-xs">/partner-portal/login</code>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Portal Email</Label>
              <Input type="email" value={credsForm.portal_email}
                     onChange={(e) => setCredsForm({ ...credsForm, portal_email: e.target.value })}
                     placeholder="partner@your-company.com"
                     data-testid="portal-creds-email" />
            </div>
            <div>
              <Label>Password (min 8 chars)</Label>
              <Input type="text" value={credsForm.password}
                     onChange={(e) => setCredsForm({ ...credsForm, password: e.target.value })}
                     placeholder="enter a new strong password"
                     data-testid="portal-creds-password" />
              <p className="text-xs text-slate-500 mt-1">Password is shown in plain text only on this screen — copy it before saving.</p>
            </div>
            {credsDialog.partner?.portal_email && (
              <div className="rounded-md bg-emerald-50 border border-emerald-200 p-2 text-xs">
                Currently set: <span className="font-mono">{credsDialog.partner.portal_email}</span>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            {credsDialog.partner?.portal_email && (
              <Button variant="outline" className="text-rose-600" onClick={revokePortalCreds}>
                Revoke Access
              </Button>
            )}
            <Button variant="outline" onClick={() => setCredsDialog({ open: false, partner: null })}>Cancel</Button>
            <Button onClick={savePortalCreds} data-testid="portal-creds-save">
              Save Credentials
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============== Wallet Dialog ==============
function WalletDialog({ partner, headers, onClose }) {
  const [mode, setMode] = useState('credit');
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [reference, setReference] = useState('');
  const [txns, setTxns] = useState([]);
  const [busy, setBusy] = useState(false);
  const [balance, setBalance] = useState(partner.wallet_balance);
  const [ledgerTab, setLedgerTab] = useState('debit');

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/partner-api/partners/${partner.partner_id}/wallet/transactions?limit=50`, { headers });
      setTxns(r.data.rows || []);
      const p = await axios.get(`${API}/api/partner-api/partners/${partner.partner_id}`, { headers });
      setBalance(p.data.wallet?.balance ?? balance);
    } catch { /* */ }
  }, [partner.partner_id, headers, balance]);

  useEffect(() => { load(); }, [load]);

  const apply = async () => {
    if (!amount || parseFloat(amount) <= 0) { toast.error('Enter positive amount'); return; }
    setBusy(true);
    try {
      const path = mode === 'credit' ? 'credit' : 'debit';
      const r = await axios.post(`${API}/api/partner-api/partners/${partner.partner_id}/wallet/${path}`, {
        amount: parseFloat(amount), notes, reference,
      }, { headers });
      toast.success(`${mode === 'credit' ? 'Credited' : 'Debited'} Rs.${amount} → balance ${fmtMoney(r.data.balance)}`);
      setAmount(''); setNotes(''); setReference('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle>Wallet — {partner.name}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-1 space-y-3">
            <Card><CardContent className="p-4">
              <p className="text-xs uppercase text-slate-500">Balance</p>
              <p className="text-2xl font-bold text-emerald-700">{fmtMoney(balance)}</p>
            </CardContent></Card>
            <Tabs value={mode} onValueChange={setMode}>
              <TabsList className="w-full grid grid-cols-2"><TabsTrigger value="credit">Credit</TabsTrigger><TabsTrigger value="debit">Debit</TabsTrigger></TabsList>
            </Tabs>
            <div><Label>Amount (Rs)</Label><Input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} data-testid="wallet-amount" /></div>
            <div><Label>Reference</Label><Input value={reference} onChange={e => setReference(e.target.value)} /></div>
            <div><Label>Notes</Label><Textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} /></div>
            <Button onClick={apply} disabled={busy} className="w-full" data-testid="wallet-apply-btn">
              {busy ? 'Processing…' : (mode === 'credit' ? 'Credit Wallet' : 'Debit Wallet')}
            </Button>
          </div>
          <div className="col-span-2">
            <p className="text-sm font-medium text-slate-700 mb-2">Transactions Ledger</p>
            {(() => {
              const debited = txns.filter(t => t.type === 'debit');
              const creditedManual = txns.filter(t => t.type === 'credit' && !t.is_refund);
              const creditedFailure = txns.filter(t => t.type === 'credit' && t.is_refund);
              const rowsFor = ledgerTab === 'debit' ? debited : ledgerTab === 'credit_manual' ? creditedManual : creditedFailure;
              return (
                <Tabs value={ledgerTab} onValueChange={setLedgerTab}>
                  <TabsList className="w-full grid grid-cols-3">
                    <TabsTrigger value="debit" data-testid="ledger-tab-debit">Debited ({debited.length})</TabsTrigger>
                    <TabsTrigger value="credit_manual" data-testid="ledger-tab-credit-manual">Credited Manually ({creditedManual.length})</TabsTrigger>
                    <TabsTrigger value="credit_failure" data-testid="ledger-tab-credit-failure">Credited for Failure ({creditedFailure.length})</TabsTrigger>
                  </TabsList>
                  <div className="border rounded max-h-[420px] overflow-y-auto mt-2" data-testid="ledger-table">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 sticky top-0"><tr>
                        <th className="text-left p-2">When</th>
                        <th className="text-right p-2">Amount</th><th className="text-right p-2">Balance</th>
                        <th className="text-left p-2">Description</th>
                      </tr></thead>
                      <tbody>
                        {rowsFor.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-slate-400">No transactions</td></tr>}
                        {rowsFor.map(t => (
                          <tr key={t.txn_id} className="border-t">
                            <td className="p-2 text-slate-500">{fmtDate(t.created_at)}</td>
                            <td className={`p-2 text-right font-semibold ${t.type === 'credit' ? 'text-emerald-700' : 'text-rose-700'}`}>{t.type === 'credit' ? '+' : '−'}{fmtMoney(t.amount)}</td>
                            <td className="p-2 text-right">{fmtMoney(t.balance_after)}</td>
                            <td className="p-2 text-slate-600">{t.description}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Tabs>
              );
            })()}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============== Activations Tab ==============
function ActivationsTab({ headers, partners }) {
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ partner_id: 'all', mobile_no: '', status: 'all' });
  const [loading, setLoading] = useState(false);
  const [inspect, setInspect] = useState(null);
  const [detail, setDetail] = useState(null);
  const [traffic, setTraffic] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [retryingId, setRetryingId] = useState(null);
  const [bulkRetrying, setBulkRetrying] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (filters.partner_id && filters.partner_id !== 'all') qs.set('partner_id', filters.partner_id);
      if (filters.mobile_no) qs.set('mobile_no', filters.mobile_no);
      if (filters.status && filters.status !== 'all') qs.set('status', filters.status);
      qs.set('limit', '100');
      const r = await axios.get(`${API}/api/partner-api/activations?${qs}`, { headers });
      setRows(r.data.rows || []);
    } catch { /* */ }
    setLoading(false);
  }, [filters, headers]);

  useEffect(() => { load(); }, [load]);

  const retryOne = async (row) => {
    setRetryingId(row.activation_id);
    try {
      const r = await axios.post(`${API}/api/partner-api/activations/${row.activation_id}/retry`, {}, { headers });
      if (r.data.success) toast.success(`Activated ${row.mobile_no} → order ${r.data.watcho_order_id || '—'}`);
      else toast.error(`Retry failed: ${r.data.error || 'Watcho rejected'}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Retry failed');
    } finally {
      setRetryingId(null);
    }
  };

  const bulkRetry = async () => {
    if (!window.confirm('Re-fire ALL failed activations from the last 24 hours? Each will re-debit the partner only if Watcho succeeds.')) return;
    setBulkRetrying(true);
    try {
      const payload = { hours: 24, limit: 200 };
      if (filters.partner_id && filters.partner_id !== 'all') payload.partner_id = filters.partner_id;
      const r = await axios.post(`${API}/api/partner-api/activations/bulk-retry`, payload, { headers });
      const d = r.data;
      toast.success(`Bulk retry done — ${d.succeeded} activated, ${d.failed} failed, ${d.skipped} skipped (of ${d.attempted})`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk retry failed');
    } finally {
      setBulkRetrying(false);
    }
  };

  const openInspector = async (row) => {
    setInspect(row);
    setDetail(null);
    setTraffic(null);
    setDetailLoading(true);
    try {
      const [d, t] = await Promise.all([
        axios.get(`${API}/api/partner-api/activations/${row.activation_id}`, { headers }),
        axios.get(`${API}/api/partner-api/activations/${row.activation_id}/traffic`, { headers }),
      ]);
      setDetail(d.data);
      setTraffic(t.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load activation');
    } finally {
      setDetailLoading(false);
    }
  };

  const verifyUpstream = async () => {
    if (!inspect) return;
    setVerifying(true);
    try {
      const r = await axios.post(
        `${API}/api/partner-api/activations/${inspect.activation_id}/verify-upstream`,
        {}, { headers },
      );
      toast.success(r.data.success ? 'Watcho confirmed transaction' : 'Watcho says: not found / failed');
      // reload detail + traffic
      const [d, t] = await Promise.all([
        axios.get(`${API}/api/partner-api/activations/${inspect.activation_id}`, { headers }),
        axios.get(`${API}/api/partner-api/activations/${inspect.activation_id}/traffic`, { headers }),
      ]);
      setDetail(d.data);
      setTraffic(t.data);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Verify failed');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Card><CardContent className="p-4 space-y-4">
      <div className="flex gap-2 flex-wrap items-end">
        <div className="w-56"><Label>Partner</Label>
          <Select value={filters.partner_id} onValueChange={v => setFilters({ ...filters, partner_id: v })}>
            <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem>{partners.map(p => <SelectItem key={p.partner_id} value={p.partner_id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="w-44"><Label>Mobile</Label><Input value={filters.mobile_no} onChange={e => setFilters({ ...filters, mobile_no: e.target.value })} /></div>
        <div className="w-40"><Label>Status</Label>
          <Select value={filters.status} onValueChange={v => setFilters({ ...filters, status: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="activated">Activated</SelectItem><SelectItem value="failed">Failed</SelectItem><SelectItem value="pending">Pending</SelectItem></SelectContent>
          </Select>
        </div>
        <Button onClick={load} disabled={loading} variant="outline"><RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh</Button>
        <Button onClick={bulkRetry} disabled={bulkRetrying} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="bulk-retry-failed-btn">
          <Zap className={`h-4 w-4 mr-1 ${bulkRetrying ? 'animate-pulse' : ''}`} />{bulkRetrying ? 'Retrying…' : 'Bulk Retry Failed (24h)'}
        </Button>
      </div>

      <div className="overflow-x-auto border rounded">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600"><tr>
            <th className="text-left p-3">When</th><th className="text-left p-3">Partner</th>
            <th className="text-left p-3">Mobile</th><th className="text-left p-3">Plan</th>
            <th className="text-right p-3">Watcho ₹</th><th className="text-right p-3">Partner ₹</th>
            <th className="text-left p-3">Status</th><th className="text-left p-3">Watcho Order</th>
            <th className="text-left p-3">Failure Reason</th>
            <th className="text-left p-3"></th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={10} className="text-center py-8 text-slate-400">No activations</td></tr>}
            {rows.map(r => (
              <tr key={r.activation_id} className="border-t hover:bg-slate-50">
                <td className="p-3 text-xs text-slate-500">{fmtDate(r.created_at)}</td>
                <td className="p-3">{r.partner_name}</td>
                <td className="p-3 font-mono">{r.mobile_no}</td>
                <td className="p-3">{r.plan_name} <span className="text-xs text-slate-400">({r.plan_id})</span></td>
                <td className="p-3 text-right">{fmtMoney(r.watcho_price)}</td>
                <td className="p-3 text-right font-semibold">{fmtMoney(r.partner_price)}</td>
                <td className="p-3"><StatusBadge status={r.provider_status} /></td>
                <td className="p-3 font-mono text-xs">{r.watcho_order_id || '-'}</td>
                <td className="p-3 text-xs text-rose-600 max-w-[280px] truncate" title={r.provider_error}>{r.provider_error || '-'}</td>
                <td className="p-3">
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openInspector(r)}
                            data-testid={`admin-inspect-${r.activation_id}`}>Inspect</Button>
                    {r.provider_status === 'failed' && (
                      <Button variant="outline" size="sm" disabled={retryingId === r.activation_id}
                              onClick={() => retryOne(r)} data-testid={`admin-retry-${r.activation_id}`}
                              className="text-amber-700 border-amber-200 hover:bg-amber-50">
                        <RotateCw className={`h-3.5 w-3.5 mr-1 ${retryingId === r.activation_id ? 'animate-spin' : ''}`} />
                        {retryingId === r.activation_id ? 'Retrying' : 'Retry'}
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AdminActivationInspector
        open={!!inspect}
        onClose={() => { setInspect(null); setDetail(null); setTraffic(null); }}
        loading={detailLoading}
        detail={detail}
        traffic={traffic}
        summary={inspect}
        onVerifyUpstream={verifyUpstream}
        verifying={verifying}
      />
    </CardContent></Card>
  );
}

// ============== Admin Activation Inspector ==============
function AdminActivationInspector({ open, onClose, loading, detail, traffic, summary, onVerifyUpstream, verifying }) {
  const act = (detail && detail.activation) || summary || {};
  const inbound = (traffic && traffic.inbound) || [];
  const outbound = (traffic && traffic.outbound) || [];
  const [expandedRow, setExpandedRow] = useState(null);
  const jsonBlock = (label, val) => (
    <div className="space-y-1">
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <pre className="bg-slate-900 text-emerald-200 text-xs p-3 rounded max-h-64 overflow-auto whitespace-pre-wrap break-all">
        {val ? JSON.stringify(val, null, 2) : '— No data —'}
      </pre>
    </div>
  );
  const Field = ({ label, value, mono }) => (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`text-sm break-all ${mono ? 'font-mono' : ''}`}>{value || '—'}</p>
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Activation Inspector
            <StatusBadge status={act.provider_status} />
          </DialogTitle>
          <DialogDescription>Forensic detail for partner debugging.</DialogDescription>
        </DialogHeader>
        {loading && !detail ? (
          <div className="py-10 flex justify-center"><RefreshCw className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : (
          <div className="space-y-4 text-sm max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-3 gap-3">
              <Field label="Activation ID" value={act.activation_id} mono />
              <Field label="Partner" value={act.partner_name} />
              <Field label="Created" value={fmtDate(act.created_at)} />
              <Field label="Mobile" value={act.mobile_no} mono />
              <Field label="Plan" value={`${act.plan_name || ''} (${act.plan_id || ''})`} />
              <Field label="Amount" value={fmtMoney(act.partner_price || act.watcho_price)} />
              <Field label="External Ref" value={act.external_ref} mono />
              <Field label="ISP Txn No" value={act.isp_txn_no} mono />
              <Field label="Watcho Order ID" value={act.watcho_order_id || '—'} mono />
              <Field label="HTTP Status (upstream)" value={act.provider_http_status || '—'} />
              <Field label="Retries" value={act.retry_count ?? 0} />
              <Field label="Refunded" value={act.refunded ? 'Yes' : 'No'} />
            </div>
            {act.provider_error ? (
              <div className="bg-rose-50 border border-rose-200 rounded p-3">
                <p className="text-xs uppercase tracking-wide text-rose-600 mb-1">Failure Reason</p>
                <p className="text-sm text-rose-800 break-words">{act.provider_error}</p>
              </div>
            ) : null}
            {jsonBlock('Request sent to Watcho', act.provider_request)}
            {jsonBlock('Response from Watcho', act.provider_response || act.provider_result)}
            {act.verify_response ? jsonBlock(`Verify Upstream (${act.verify_checked_at?.slice(0, 19).replace('T', ' ') || ''})`, act.verify_response) : null}

            <TrafficSection
              title={`Inbound — Partner → CRM (${inbound.length})`}
              description="HTTP hits the partner made to crm.antbroadband.com tied to this activation"
              rows={inbound}
              kind="inbound"
              expandedRow={expandedRow}
              setExpandedRow={setExpandedRow}
            />
            <TrafficSection
              title={`Outbound — CRM → Watcho (${outbound.length})`}
              description="Watcho HTTP calls our backend made for this activation (incl. retries + verifications)"
              rows={outbound}
              kind="outbound"
              expandedRow={expandedRow}
              setExpandedRow={setExpandedRow}
            />
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="admin-inspect-close">Close</Button>
          {act.isp_txn_no ? (
            <Button onClick={onVerifyUpstream} disabled={verifying}
                    data-testid="admin-inspect-verify">
              {verifying ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-1" />}
              Verify with Watcho
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============== Traffic Section (used inside Activation Inspector) ==============
function TrafficSection({ title, description, rows, kind, expandedRow, setExpandedRow }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="space-y-1">
        <p className="text-xs uppercase tracking-wide text-slate-500">{title}</p>
        <p className="text-xs text-slate-400">{description} — no records.</p>
      </div>
    );
  }
  const isInbound = kind === 'inbound';
  return (
    <div className="space-y-1">
      <p className="text-xs uppercase tracking-wide text-slate-500">{title}</p>
      <p className="text-[11px] text-slate-400 mb-1">{description}</p>
      <div className="border rounded overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left p-2">When</th>
              {isInbound ? <th className="text-left p-2">IP</th> : <th className="text-left p-2">Endpoint</th>}
              {isInbound ? <th className="text-left p-2">Path</th> : <th className="text-left p-2">Outcome</th>}
              <th className="text-left p-2">Status</th>
              <th className="text-right p-2">Latency</th>
              <th className="text-left p-2">Summary</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const rowKey = `${kind}-${i}`;
              const isExp = expandedRow === rowKey;
              return (
                <React.Fragment key={rowKey}>
                  <tr className="border-t hover:bg-slate-50 cursor-pointer"
                      onClick={() => setExpandedRow(isExp ? null : rowKey)}>
                    <td className="p-2 text-slate-500">{fmtDate(r.created_at)}</td>
                    {isInbound ? (
                      <td className="p-2 font-mono">{r.ip || '-'}</td>
                    ) : (
                      <td className="p-2 font-mono">{r.endpoint || '-'}</td>
                    )}
                    {isInbound ? (
                      <td className="p-2 font-mono truncate max-w-[260px]" title={r.path}>{r.method} {r.path}</td>
                    ) : (
                      <td className="p-2">
                        <Badge className={
                          r.outcome === 'ok' ? 'bg-emerald-100 text-emerald-700' :
                          r.outcome === 'http_error' ? 'bg-amber-100 text-amber-700' :
                          'bg-rose-100 text-rose-700'
                        }>{r.outcome}</Badge>
                      </td>
                    )}
                    <td className="p-2">{isInbound ? r.response_status : r.http_status}</td>
                    <td className="p-2 text-right">{r.duration_ms ?? '-'}ms</td>
                    <td className="p-2 truncate max-w-[260px]" title={r.response_summary || r.error || ''}>
                      {isInbound ? (r.response_summary || '-') : (r.error || `attempt ${r.attempt}`)}
                    </td>
                  </tr>
                  {isExp && (
                    <tr className="bg-slate-50">
                      <td colSpan={6} className="p-3">
                        {isInbound ? (
                          <div className="space-y-2">
                            <div className="grid grid-cols-2 gap-3 text-xs">
                              <div><span className="text-slate-500">x-api-key:</span> <span className="font-mono">{r.request_headers?.['x-api-key'] || '-'}</span></div>
                              <div><span className="text-slate-500">x-timestamp:</span> <span className="font-mono">{r.request_headers?.['x-timestamp'] || '-'}</span></div>
                              <div><span className="text-slate-500">x-signature:</span> <span className="font-mono">{r.request_headers?.['x-signature'] || '-'}</span></div>
                              <div><span className="text-slate-500">user-agent:</span> <span className="font-mono break-all">{r.request_headers?.['user-agent'] || '-'}</span></div>
                            </div>
                            {r.auth_error ? (
                              <p className="text-xs text-rose-600">Auth error: {r.auth_error}</p>
                            ) : null}
                            <div>
                              <p className="text-[10px] uppercase text-slate-500 mb-1">Request body received</p>
                              <pre className="bg-slate-900 text-emerald-200 text-xs p-2 rounded max-h-48 overflow-auto whitespace-pre-wrap break-all">{r.request_body || '— empty —'}</pre>
                            </div>
                            <div>
                              <p className="text-[10px] uppercase text-slate-500 mb-1">Response sent back</p>
                              <pre className="bg-slate-900 text-sky-200 text-xs p-2 rounded max-h-48 overflow-auto whitespace-pre-wrap break-all">{r.response_body || '— not captured —'}</pre>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <div className="grid grid-cols-2 gap-3 text-xs">
                              <div><span className="text-slate-500">URL:</span> <span className="font-mono break-all">{r.url}</span></div>
                              <div><span className="text-slate-500">Proxy:</span> <span className="font-mono">{r.proxy || 'direct'}</span></div>
                              <div><span className="text-slate-500">Triggered by:</span> <span className="font-mono">{r.triggered_by || '-'}</span></div>
                              <div><span className="text-slate-500">Mobile / ISP Txn:</span> <span className="font-mono">{r.mobile_no || '-'} / {r.isp_txn_no || '-'}</span></div>
                            </div>
                            <div>
                              <p className="text-[10px] uppercase text-slate-500 mb-1">Request payload (plaintext, before 3DES)</p>
                              <pre className="bg-slate-900 text-emerald-200 text-xs p-2 rounded max-h-48 overflow-auto whitespace-pre-wrap break-all">{JSON.stringify(r.request_plaintext, null, 2)}</pre>
                            </div>
                            <div>
                              <p className="text-[10px] uppercase text-slate-500 mb-1">Encrypted payload (truncated)</p>
                              <pre className="bg-slate-900 text-amber-200 text-xs p-2 rounded max-h-24 overflow-auto break-all">{r.request_encrypted || '-'}</pre>
                            </div>
                            <div>
                              <p className="text-[10px] uppercase text-slate-500 mb-1">Raw HTTP response from Watcho</p>
                              <pre className="bg-slate-900 text-sky-200 text-xs p-2 rounded max-h-48 overflow-auto whitespace-pre-wrap break-all">{r.response_raw || '— empty —'}</pre>
                            </div>
                            {r.response_decoded ? (
                              <div>
                                <p className="text-[10px] uppercase text-slate-500 mb-1">Decoded response</p>
                                <pre className="bg-slate-900 text-emerald-200 text-xs p-2 rounded max-h-48 overflow-auto whitespace-pre-wrap break-all">{JSON.stringify(r.response_decoded, null, 2)}</pre>
                              </div>
                            ) : null}
                            {r.error ? <p className="text-xs text-rose-600">Error: {r.error}</p> : null}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============== Logs Tab ==============
function LogsTab({ headers, partners }) {
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ partner_id: 'all', path: '', failed: false });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (filters.partner_id && filters.partner_id !== 'all') qs.set('partner_id', filters.partner_id);
      if (filters.path) qs.set('path', filters.path);
      if (filters.failed) qs.set('status_min', '400');
      qs.set('limit', '150');
      const r = await axios.get(`${API}/api/partner-api/api-logs?${qs}`, { headers });
      setRows(r.data.rows || []);
    } catch { /* */ }
    setLoading(false);
  }, [filters, headers]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card><CardContent className="p-4 space-y-4">
      <div className="flex gap-2 flex-wrap items-end">
        <div className="w-56"><Label>Partner</Label>
          <Select value={filters.partner_id} onValueChange={v => setFilters({ ...filters, partner_id: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem>{partners.map(p => <SelectItem key={p.partner_id} value={p.partner_id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="w-56"><Label>Endpoint contains</Label><Input value={filters.path} onChange={e => setFilters({ ...filters, path: e.target.value })} placeholder="subscribe / current-plan" /></div>
        <div className="flex items-center gap-2 pb-2"><Switch checked={filters.failed} onCheckedChange={c => setFilters({ ...filters, failed: c })} /><Label>Only failures (≥400)</Label></div>
        <Button onClick={load} disabled={loading} variant="outline"><RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh</Button>
      </div>

      <div className="overflow-x-auto border rounded">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 uppercase text-slate-600"><tr>
            <th className="text-left p-2">When</th><th className="text-left p-2">Partner</th>
            <th className="text-left p-2">IP</th><th className="text-left p-2">Method</th>
            <th className="text-left p-2">Path</th><th className="text-left p-2">Status</th>
            <th className="text-right p-2">Latency</th><th className="text-left p-2">Summary</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-slate-400">No log entries</td></tr>}
            {rows.map((r, i) => (
              <tr key={i} className="border-t">
                <td className="p-2 text-slate-500">{fmtDate(r.created_at)}</td>
                <td className="p-2 text-slate-700">{(partners.find(p => p.partner_id === r.partner_id) || {}).name || (r.partner_id ? r.partner_id.substring(0, 6) : '-')}</td>
                <td className="p-2 font-mono">{r.ip}</td>
                <td className="p-2">{r.method}</td>
                <td className="p-2 font-mono">{r.path}</td>
                <td className={`p-2 font-semibold ${r.response_status >= 400 ? 'text-rose-700' : 'text-emerald-700'}`}>{r.response_status}</td>
                <td className="p-2 text-right">{r.duration_ms}ms</td>
                <td className="p-2 text-slate-600 max-w-md truncate" title={r.response_summary}>{r.response_summary || r.auth_error}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CardContent></Card>
  );
}

// ============== Docs Tab ==============
function DocsTab() {
  const { token } = useAuth();

  const download = async (slug, suggestedName) => {
    try {
      const res = await fetch(`${API}/api/partner-api/docs/${slug}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        toast.error(`Download failed: ${res.status}`);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = suggestedName;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${suggestedName} downloaded`);
    } catch (e) {
      toast.error('Network error');
    }
  };

  const example = `# 1. Build signature (Python example)
import time, json, hashlib, hmac, requests

API_KEY    = "pk_..."          # from admin
API_SECRET = "sk_..."          # from admin
BASE       = "https://crm.antbroadband.com"

def call(method, path, body=None):
    body_b = json.dumps(body).encode() if body else b""
    ts     = str(int(time.time()))
    sig    = hmac.new(
        API_SECRET.encode(),
        f"{method}\\n{path}\\n{ts}\\n{hashlib.sha256(body_b).hexdigest()}".encode(),
        hashlib.sha256
    ).hexdigest()
    return requests.request(method, BASE + path, data=body_b if body else None,
        headers={
            "Content-Type": "application/json",
            "X-API-Key": API_KEY,
            "X-Timestamp": ts,
            "X-Signature": sig,
        })

# 2. Subscribe
r = call("POST", "/api/external/watcho/subscribe", {
    "mobile_no": "9876543210",
    "plan_id": 170475,
    "external_ref": "MY_ORDER_001"   # optional, idempotency key
})
print(r.json())`;

  return (
    <Card><CardContent className="p-6 space-y-4 text-sm">
      <div className="flex items-center justify-between flex-wrap gap-3 pb-2 border-b">
        <h3 className="text-lg font-semibold">Integration Guide for Partners</h3>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => download('api-doc', 'ANT-Partner-API-v1.md')} data-testid="download-api-doc-btn">
            <FileText className="h-4 w-4 mr-1" /> Download API Doc (.md)
          </Button>
          <Button variant="outline" size="sm" onClick={() => download('postman', 'ANT-Partner-API.postman_collection.json')} data-testid="download-postman-btn">
            <FileText className="h-4 w-4 mr-1" /> Download Postman Collection
          </Button>
        </div>
      </div>

      <p className="text-xs text-slate-500">
        Share the full API doc (.md) and the Postman collection with your partner&apos;s developer.
        The Postman collection auto-signs every request — partner only needs to plug in their <code>api_key</code> + <code>api_secret</code> in the collection variables.
      </p>

      <div>
        <p className="font-semibold mb-1">Base URL</p>
        <code className="bg-slate-100 px-2 py-1 rounded">https://crm.antbroadband.com</code>
      </div>

      <div>
        <p className="font-semibold mb-1">Authentication (per request)</p>
        <ul className="list-disc list-inside text-slate-700 space-y-1">
          <li>Source IP must be in the partner&apos;s whitelist (configured by the admin).</li>
          <li><code>X-API-Key</code> header — partner&apos;s public key (<code>pk_…</code>).</li>
          <li><code>X-Timestamp</code> header — UNIX seconds, must be within ±5 min of server time.</li>
          <li><code>X-Signature</code> header — hex HMAC-SHA256 over the canonical string below.</li>
        </ul>
        <pre className="bg-slate-100 p-3 rounded mt-2 text-xs overflow-x-auto">{`signature_input = "{METHOD}\\n{PATH_WITH_QUERY}\\n{TIMESTAMP}\\n{SHA256(body_bytes)}"
signature        = HMAC_SHA256(api_secret, signature_input).hexdigest()`}</pre>
      </div>

      <div>
        <p className="font-semibold mb-1">Endpoints</p>
        <ul className="list-disc list-inside text-slate-700 space-y-1">
          <li><code>POST /api/external/watcho/subscribe</code> — create activation</li>
          <li><code>GET&nbsp; /api/external/watcho/current-plan?mobile_no=…</code> — current plan lookup</li>
          <li><code>POST /api/external/watcho/unsubscribe</code> — terminate plan</li>
          <li><code>GET&nbsp; /api/external/watcho/transactions</code> — partner&apos;s own history</li>
          <li><code>POST /api/external/watcho/retry/{`{activation_id}`}</code> — retry a failed activation</li>
        </ul>
      </div>

      <div>
        <p className="font-semibold mb-1">Errors</p>
        <ul className="list-disc list-inside text-slate-700 space-y-1">
          <li>401 — invalid API key / signature / timestamp drift</li>
          <li>403 — IP not whitelisted</li>
          <li>402 — insufficient wallet balance</li>
          <li>429 — rate-limit exceeded (default 60/min)</li>
          <li>502 — Watcho upstream error (wallet auto-refunded; retry endpoint available)</li>
        </ul>
      </div>

      <div>
        <p className="font-semibold mb-1">Reference implementation (Python)</p>
        <pre className="bg-slate-900 text-emerald-100 p-4 rounded text-xs overflow-x-auto">{example}</pre>
      </div>
    </CardContent></Card>
  );
}
